// skipcq: JS-W1028
import { useCallback, useEffect, useState } from 'react';
import {
  listIngestionFailures,
  resolveIngestionFailure,
  retryIngestionFailure,
} from '../services/beerIngestionService';
import {
  captureBeerIngestionFailuresEmpty,
  captureBeerIngestionFailuresError,
  captureBeerIngestionFailuresRetry,
  captureBeerIngestionFailuresResolve,
  captureBeerIngestionFailuresView,
} from '../utils/beerIngestionAnalytics';

const EMPTY_SUMMARY = {
  open: 0,
  resolved: 0,
  autoPausedCount: 0,
  byCategory: {},
};

export default function useBeerIngestionFailures(stateCode) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(Boolean(stateCode));
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingItemId, setPendingItemId] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const refresh = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!stateCode) {
      return undefined;
    }
    let cancelled = false;
    const loadRows = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await listIngestionFailures(stateCode);
        if (cancelled) {
          return;
        }
        setRows(result.rows);
        setSummary(result.summary);
        captureBeerIngestionFailuresView(stateCode, result.summary?.open ?? 0);
        if (result.rows.length === 0) {
          captureBeerIngestionFailuresEmpty(stateCode);
        }
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        captureBeerIngestionFailuresError(stateCode);
        setError(loadError?.message || 'Failed to load ingestion failures');
        setRows([]);
        setSummary(EMPTY_SUMMARY);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadRows();
    return () => {
      cancelled = true;
    };
  }, [stateCode, reloadToken]);

  const resolveFailure = useCallback(async (itemId) => {
    setActionLoading(true);
    setActionError(null);
    setPendingItemId(itemId);
    try {
      await resolveIngestionFailure(itemId);
      captureBeerIngestionFailuresResolve(itemId);
      refresh();
    } catch (resolveError) {
      setActionError(resolveError?.message || 'Failed to resolve failure');
      throw resolveError;
    } finally {
      setActionLoading(false);
      setPendingItemId(null);
    }
  }, [refresh]);

  const retryFailure = useCallback(async (item) => {
    if (!item?.itemId) {
      return null;
    }
    setActionLoading(true);
    setActionError(null);
    setPendingItemId(item.itemId);
    try {
      const result = await retryIngestionFailure(item.itemId);
      captureBeerIngestionFailuresRetry(item.itemId, item.category, item.breweryId, item.sourceId);
      refresh();
      return result;
    } catch (retryError) {
      setActionError(retryError?.message || 'Failed to retry failure');
      throw retryError;
    } finally {
      setActionLoading(false);
      setPendingItemId(null);
    }
  }, [refresh]);

  if (!stateCode) {
    return {
      rows: [],
      summary: EMPTY_SUMMARY,
      loading: false,
      error: null,
      actionError: null,
      actionLoading: false,
      pendingItemId: null,
      refresh,
      resolveFailure,
      retryFailure,
    };
  }

  return {
    rows,
    summary,
    loading,
    error,
    actionError,
    actionLoading,
    pendingItemId,
    refresh,
    resolveFailure,
    retryFailure,
  };
}
